const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


/**
 * Fetch job circulars with location mapping
 * @param {Array} locations - Array of location objects for mapping IDs to names
 * @returns {Promise<Object>} Response object with job data
 */
export const getJobCirculars = async (locations = []) => {
  try {
    // Using external Laravel API which should have complete data
    const response = await fetch(`${API_BASE_URL}/api/v1/job-circulars`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    
    if (data.status === 'success') {
      return {
        success: true,
        count: data.count,
        jobs: data.data.map(job => formatJobData(job, locations))
      };
    } else {
      throw new Error('API response indicates failure');
    }
  } catch (error) {
    console.error('Error fetching job circulars:', error);
    return {
      success: false,
      error: error.message,
      jobs: []
    };
  }
};

/**
 * Fetch a single job circular by ID
 * @param {number} jobId - The job ID to fetch
 * @param {Array} locations - Array of location objects for mapping IDs to names
 * @returns {Promise<Object>} Response object with single job data
 */
export const getJobCircularById = async (jobId, locations = []) => {
  try {
    // Since the API doesn't have a single job endpoint, fetch all jobs and filter by ID
    const jobsResult = await getJobCirculars(locations);
    
    if (jobsResult.success && jobsResult.jobs) {
      // Find the job with matching ID (convert both to string for comparison)
      const job = jobsResult.jobs.find(j => String(j.id) === String(jobId) || j.encoded_id === jobId);
      
      if (job) {
        return {
          success: true,
          job: job
        };
      } else {
        throw new Error('Job not found');
      }
    } else {
      throw new Error(jobsResult.error || 'Failed to fetch jobs');
    }
  } catch (error) {
    console.error('Error fetching job circular:', error);
    return {
      success: false,
      error: error.message,
      job: null
    };
  }
};

// Helper function to format job data from API to match component expectations
const formatJobData = (apiJob, locations = []) => {
  // Map location ID to location name
  const getLocationName = (locationId) => {
    if (!locationId || !locations.length) {
      return apiJob.job_location || 'Location not specified';
    }
    
    const location = locations.find(loc => loc.id === parseInt(locationId));
    return location ? location.name : (apiJob.job_location || 'Location not found');
  };

  return {
    id: apiJob.id,
    category_id: apiJob.category_id,
    team_id: apiJob.team_id,
    team_name: apiJob.team_name,
    member_id: apiJob.member_id,
    circular_type: apiJob.circular_type,
    type: formatJobType(apiJob.circular_type),
    job_title: apiJob.job_title,
    title: apiJob.job_title, // Alias for backward compatibility
    slug: apiJob.slug,
    job_context: apiJob.job_context,
    responsibility: apiJob.responsibility,
    job_requirement: apiJob.job_requirement,
    education_requirement: apiJob.education_requirement,
    benefits: apiJob.benefits,
    job_location: apiJob.job_location,
    location: getLocationName(apiJob.location_id || apiJob.job_location),
    locationId: apiJob.location_id,
    job_experience: apiJob.job_experience,
    experience: apiJob.job_experience, // Alias for backward compatibility
    deadline: apiJob.deadline,
    duration: apiJob.duration,
    period: formatDuration(apiJob.duration), // Alias for backward compatibility
    salary: formatSalary(apiJob.salary),
    job_vacancy: apiJob.job_vacancy,
    sitting_place: apiJob.sitting_place,
    place_remark: apiJob.place_remark,
    device_availability: apiJob.device_availability,
    device_remark: apiJob.device_remark,
    status: apiJob.status,
    created_at: apiJob.created_at,
    created_by: apiJob.created_by,
    updated_at: apiJob.updated_at,
    updated_by: apiJob.updated_by,
    category_name: apiJob.name,
    encoded_id: apiJob.encoded_id
  };
};

// Format job type for display
const formatJobType = (circularType) => {
  switch (circularType) {
    case 'job_circular':
      return 'Fulltime';
    case 'internship':
      return 'Internship';
    default:
      return 'Contract';
  }
};

// Format salary for display
const formatSalary = (salary) => {
  if (!salary || salary === '0' || salary === 'Negotiable') {
    return 'Negotiable';
  }
  return salary;
};

// Format duration to match existing periods
const formatDuration = (duration) => {
  switch (duration) {
    case 'Full Time':
      return 'Monthly';
    case 'Part Time':
      return 'Hourly';
    default:
      return 'Monthly';
  }
};
